const OBJECT_ID_PATTERN = /^[a-fA-F0-9]{24}$/

const idFrom = value => {
  if (value == null || value === '') return ''
  if (typeof value === 'object') {
    return String(value._id || value.id || '').trim()
  }
  return String(value).trim()
}

export const uniqueStockTakeSupplierIds = values => {
  if (!Array.isArray(values)) return []
  const unique = []
  const seen = new Set()
  values.forEach(value => {
    const id = idFrom(value)
    if (!OBJECT_ID_PATTERN.test(id) || seen.has(id)) return
    seen.add(id)
    unique.push(id)
  })
  return unique
}

export const stockTakePartialGroupId = stockTake => {
  const id = idFrom(
    stockTake?.partialGroupId?._id ||
      stockTake?.partialGroupId ||
      stockTake?.partialGroup?._id ||
      stockTake?.partialGroup,
  )
  return OBJECT_ID_PATTERN.test(id) ? id : ''
}

export const stockTakeCreateRequestPath = (venueId, data) => {
  const groupId = stockTakePartialGroupId(data)
  const suppliers = uniqueStockTakeSupplierIds(data?.supplierIds)
  if (groupId && !suppliers.length) {
    return `venue/${venueId}/stocks/groups/${groupId}/start`
  }
  return `venue/${venueId}/stocks`
}

export const toggleStockTakeCreateSupplierId = (current, supplierId) => {
  const ids = uniqueStockTakeSupplierIds(current)
  const id = uniqueStockTakeSupplierIds([supplierId])[0]
  if (!id) return ids
  return ids.includes(id) ? ids.filter(item => item !== id) : [...ids, id]
}

export const stockTakeCreateSuppliersSelectedLine = count => {
  const n = Number(count) || 0
  if (n <= 0) return ''
  if (n === 1) return '1 supplier selected'
  return `${n} suppliers selected`
}

export const canSubmitStockTakeCreate = ({
  startDate,
  endDate,
  supplierIds,
} = {}) => {
  if (uniqueStockTakeSupplierIds(supplierIds).length) return true
  return Boolean(startDate && endDate)
}

export const STOCK_TAKE_LIST_OPEN_LABEL = 'Open'

export const STOCK_TAKE_LIST_REPORT_LOCKED_HINT = 'Lock to download'

export const STOCK_TAKE_LIST_REPORT_PRO_HINT = 'Pro report'

/** Quiet reason when Stock / Wastage report cannot open (open take = lock first). */
export const stockTakeListReportDisabledCopy = ({
  isLocked,
  canUseReports,
} = {}) => {
  if (!canUseReports) return STOCK_TAKE_LIST_REPORT_PRO_HINT
  if (!isLocked) return STOCK_TAKE_LIST_REPORT_LOCKED_HINT
  return ''
}

export const STOCK_TAKE_LIST_SUBTITLE =
  "Count what's on the shelf. We'll tell you what to order."

export const STOCK_TAKE_JOB_SUPPLIER =
  'Count a supplier to order to par — a short count, not the full report.'

export const STOCK_TAKE_JOB_HOUSE =
  'Count the whole house for wastage, sold, and reports.'

export const STOCK_TAKE_CREATE_PATHS_HINT = `${STOCK_TAKE_JOB_SUPPLIER} ${STOCK_TAKE_JOB_HOUSE}`

export const STOCK_TAKE_CREATE_SUPPLIER_HEADING = 'Count a supplier to order'

export const STOCK_TAKE_CREATE_SUPPLIER_BODY =
  'Stock vs par for the suppliers you pick, then order the gap. You will not be asked for sold, wastage, or the rest of the house.'

export const STOCK_TAKE_CREATE_HOUSE_HEADING = 'Count the whole house'

export const STOCK_TAKE_CREATE_HOUSE_BODY =
  'The full stock take — sold, wastage, and reports. Use this when you need the complete picture, not just an order.'

export const STOCK_TAKE_CREATE_WHY_DATES =
  'Clear the dates to count a supplier to order.'

export const STOCK_TAKE_CREATE_WHY_SUPPLIERS =
  'Clear suppliers to run the full-house take.'

export const STOCK_TAKE_CREATE_CTA_HINT = 'Choose suppliers or a date range.'

export const STOCK_TAKE_CREATE_DATES_LABEL = 'Date range'

export const STOCK_TAKE_CREATE_SUPPLIERS_LABEL = 'Suppliers'

export const STOCK_TAKE_CREATE_SUPPLIERS_PLACEHOLDER =
  'Choose one or more suppliers'

export const stockTakeCreateHasDates = ({ startDate, endDate } = {}) =>
  Boolean(startDate || endDate)

export const stockTakeCreateHasSuppliers = ({ supplierIds } = {}) =>
  uniqueStockTakeSupplierIds(supplierIds).length > 0

/** Supplier path wins: dates are unused. */
export const isStockTakeCreateDatesDisabled = args =>
  stockTakeCreateHasSuppliers(args)

/** Date path: suppliers unused until the period is cleared. */
export const isStockTakeCreateSuppliersDisabled = args =>
  stockTakeCreateHasDates(args) && !stockTakeCreateHasSuppliers(args)

export const MAIN_STOCK_TAKE_REASON = 'Main Stock Take'
export const PARTIAL_STOCK_TAKE_REASON = 'Partial Count'

/** Frozen OTP E2E take — do not PATCH dummy notes unless asked. */
export const STOCK_TAKE_LEAVE_UNLOCKED_STAGING_ID = '6ac68407f4eb466f39f901bb'

const DUMMY_STOCK_TAKE_NOTE_VALUES = new Set(['test', 'test 2'])
export const STOCK_TAKE_DUMMY_NOTE_FIELDS = [
  'siteManager',
  'lineManager',
  'countNotes',
]

export const isDummyStockTakeNote = value =>
  DUMMY_STOCK_TAKE_NOTE_VALUES.has(
    String(value || '')
      .trim()
      .toLowerCase(),
  )

export const patchDummyStockTakeNotes = (stockTake = {}) => {
  const patch = {}
  STOCK_TAKE_DUMMY_NOTE_FIELDS.forEach(field => {
    if (isDummyStockTakeNote(stockTake?.[field])) patch[field] = ''
  })
  return patch
}

export const shouldPersistDummyStockTakeNoteClear = stockTake => {
  if (!stockTake?._id) return false
  if (String(stockTake._id) === STOCK_TAKE_LEAVE_UNLOCKED_STAGING_ID) {
    return false
  }
  return Object.keys(patchDummyStockTakeNotes(stockTake)).length > 0
}

/** One line: Partial is a real count you order from; reports stay on Main. */
export const STOCK_TAKE_MAIN_VS_PARTIAL_LINE =
  'Main is the full house count for wastage and reports. A supplier count is the quick count you order from — stock vs par only. Reports still use Main only.'

export const STOCK_TAKE_QUIET_DRAWER_TITLE = 'Groups, access, and email'

/** Groups are a fridge/wet count you repeat, not an order path. */
export const STOCK_TAKE_GROUPS_WHY_LINE =
  'Groups are for repeating the same fridge or wet count, not for ordering.'

export const stockTakeNameFromSupplierTitles = (titles = []) => {
  const seen = new Set()
  const names = []
  ;(Array.isArray(titles) ? titles : []).forEach(title => {
    const next = String(title || '').trim()
    if (!next || seen.has(next)) return
    seen.add(next)
    names.push(next)
  })
  return names.join(', ')
}

export const supplierTitlesForCreateIds = (supplierIds, options = []) => {
  const ids = uniqueStockTakeSupplierIds(supplierIds)
  const labelById = new Map(
    (Array.isArray(options) ? options : []).map(option => [
      option?.value,
      option?.label,
    ]),
  )
  return ids.map(id => labelById.get(id)).filter(Boolean)
}

/**
 * Dashboard create body. No suppliers → current date-range create.
 * Suppliers → Express seeds a partial count (dates optional; today on the API).
 * Supplier counts: Partial reason, supplier title if name empty, no dummy notes.
 */
export const buildStockTakeCreatePayload = ({
  startDate,
  endDate,
  name,
  supplierIds,
  supplierTitles,
  countMetadata = {},
  prevStockTake,
  reasonForCount = MAIN_STOCK_TAKE_REASON,
} = {}) => {
  const ids = uniqueStockTakeSupplierIds(supplierIds)
  const trimmedName = String(name || '').trim()

  if (ids.length) {
    const fromSuppliers = stockTakeNameFromSupplierTitles(supplierTitles)
    const payload = {
      supplierIds: ids,
      reasonForCount:
        countMetadata.reasonForCount &&
        countMetadata.reasonForCount !== MAIN_STOCK_TAKE_REASON
          ? String(countMetadata.reasonForCount).trim()
          : PARTIAL_STOCK_TAKE_REASON,
    }
    if (trimmedName) payload.name = trimmedName
    else if (fromSuppliers) payload.name = fromSuppliers
    if (startDate) payload.startDate = startDate
    if (endDate) payload.endDate = endDate
    return payload
  }

  const payload = {
    ...countMetadata,
    reasonForCount: countMetadata.reasonForCount || reasonForCount,
  }
  if (trimmedName) payload.name = trimmedName
  payload.startDate = startDate
  payload.endDate = endDate
  if (prevStockTake) payload.prevStockTake = prevStockTake
  return payload
}

export const stockTakeLastSuppliersStorageKey = venueId =>
  `oms:stockTakeLastSupplierIds:${venueId}`

const venueSupplierIdSet = venueSupplierIds =>
  new Set(uniqueStockTakeSupplierIds(venueSupplierIds))

export const keepStockTakeSuppliersOnVenue = (
  supplierIds,
  venueSupplierIds,
) => {
  const onVenue = venueSupplierIdSet(venueSupplierIds)
  return uniqueStockTakeSupplierIds(supplierIds).filter(id => onVenue.has(id))
}

export const lastSupplierIdsFromStockTakes = (stockTakes = []) => {
  const latest = (Array.isArray(stockTakes) ? stockTakes : []).find(
    stockTake => uniqueStockTakeSupplierIds(stockTake?.supplierIds).length,
  )
  return uniqueStockTakeSupplierIds(latest?.supplierIds)
}

export const resolveRememberedStockTakeSupplierIds = ({
  rememberedIds,
  lastStockTakeSupplierIds,
  venueSupplierIds,
} = {}) => {
  const rememberedOnVenue = keepStockTakeSuppliersOnVenue(
    rememberedIds,
    venueSupplierIds,
  )
  if (rememberedOnVenue.length) return rememberedOnVenue
  return keepStockTakeSuppliersOnVenue(
    lastStockTakeSupplierIds,
    venueSupplierIds,
  )
}

export const readLastStockTakeSupplierIds = (
  venueId,
  storage = typeof localStorage === 'undefined' ? null : localStorage,
) => {
  if (!venueId || !storage) return []
  try {
    const raw = storage.getItem(stockTakeLastSuppliersStorageKey(venueId))
    if (!raw) return []
    return uniqueStockTakeSupplierIds(JSON.parse(raw))
  } catch {
    return []
  }
}

export const writeLastStockTakeSupplierIds = (
  venueId,
  supplierIds,
  storage = typeof localStorage === 'undefined' ? null : localStorage,
) => {
  const ids = uniqueStockTakeSupplierIds(supplierIds)
  if (!venueId || !storage || !ids.length) return ids
  try {
    storage.setItem(
      stockTakeLastSuppliersStorageKey(venueId),
      JSON.stringify(ids),
    )
  } catch {
    // Private mode / quota — still return ids for this session.
  }
  return ids
}

const ukCalendarDay = value => {
  if (value == null || value === '') return ''
  const date = value instanceof Date ? value : new Date(value)
  if (!Number.isFinite(date.getTime())) return ''
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}

const ukDayLabel = value => {
  const date = value instanceof Date ? value : new Date(value)
  if (!Number.isFinite(date.getTime())) return ''
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

/**
 * Repeat: new blank supplier or group count, same name and scope.
 * Never copies quantities or prevStockTake.
 */
export const buildStockTakeRepeatPayload = (
  stockTake,
  { venueSupplierIds } = {},
) => {
  const supplierIds = keepStockTakeSuppliersOnVenue(
    stockTake?.supplierIds,
    venueSupplierIds ?? stockTake?.supplierIds,
  )
  if (supplierIds.length) {
    return buildStockTakeCreatePayload({
      name: stockTake?.name,
      supplierIds,
      supplierTitles: stockTake?.supplierTitles,
    })
  }
  const partialGroupId = stockTakePartialGroupId(stockTake)
  if (!partialGroupId) return null
  const groupName =
    String(stockTake?.name || '').trim() ||
    String(
      stockTake?.partialGroupName || stockTake?.partialGroup?.name || '',
    ).trim()
  return {
    countType: 'PARTIAL',
    partialGroupId,
    reasonForCount: PARTIAL_STOCK_TAKE_REASON,
    ...(groupName ? { name: groupName } : {}),
  }
}

export const canRepeatStockTake = (stockTake, options) =>
  Boolean(buildStockTakeRepeatPayload(stockTake, options))

export const stockTakeRepeatSupplierKey = stockTake => {
  const ids = uniqueStockTakeSupplierIds(stockTake?.supplierIds)
  return ids.length ? `id:${[...ids].sort().join(',')}` : ''
}

export const stockTakeRepeatIdentityKey = stockTake => {
  const suppliers = stockTakeRepeatSupplierKey(stockTake)
  if (suppliers) return suppliers
  const groupId = stockTakePartialGroupId(stockTake)
  return groupId ? `group:${groupId}` : ''
}

/** Unlocked live count for the same suppliers or group (list is newest-first). */
export const findOpenStockTakeForRepeat = (
  stockTake,
  stockTakes = [],
  options,
) => {
  const payload = buildStockTakeRepeatPayload(stockTake, options)
  if (!payload) return null
  const key = stockTakeRepeatIdentityKey(payload)
  if (!key) return null
  const list = Array.isArray(stockTakes) ? stockTakes : []
  return (
    list.find(
      item => !item?.isLocked && stockTakeRepeatIdentityKey(item) === key,
    ) || null
  )
}

export const distinguishStockTakeRepeatPayload = (
  payload,
  stockTakes = [],
  { now = new Date() } = {},
) => {
  const key = stockTakeRepeatIdentityKey(payload)
  if (!key) return payload
  const today = ukCalendarDay(now)
  const clashes = (Array.isArray(stockTakes) ? stockTakes : []).some(item => {
    if (stockTakeRepeatIdentityKey(item) !== key) return false
    const day = ukCalendarDay(item?.fromPeriod || item?.toPeriod)
    return day === today
  })
  if (!clashes) return payload
  const label = ukDayLabel(now)
  const base =
    String(payload.name || '').trim() ||
    (payload.partialGroupId ? 'Group count' : 'Supplier count')
  if (base.includes(label)) return payload
  return { ...payload, name: `${base} · ${label}` }
}

/**
 * Repeat / Count again: continue the live take, or create one that the list
 * can tell apart from today’s 01 Cookies.
 */
export const resolveStockTakeRepeatAction = (
  stockTake,
  stockTakes = [],
  options = {},
) => {
  const payload = buildStockTakeRepeatPayload(stockTake, options)
  if (!payload) return null
  const open = findOpenStockTakeForRepeat(stockTake, stockTakes, options)
  if (open) return { kind: 'continue', stockTake: open }
  return {
    kind: 'create',
    payload: distinguishStockTakeRepeatPayload(payload, stockTakes, options),
  }
}

/** Most recent supplier count that can be repeated (list is newest-first). */
export const pickLatestRepeatableStockTake = (stockTakes = [], options) => {
  const list = Array.isArray(stockTakes) ? stockTakes : []
  return list.find(stockTake => canRepeatStockTake(stockTake, options)) || null
}

const createPrefillClashesToday = (payload, stockTakes = [], now) => {
  if (!payload?.supplierIds?.length) return false
  const key = `id:${[...payload.supplierIds].sort().join(',')}`
  const today = ukCalendarDay(now)
  if (!today) return false
  return (Array.isArray(stockTakes) ? stockTakes : []).some(item => {
    if (stockTakeRepeatSupplierKey(item) !== key) return false
    return ukCalendarDay(item?.fromPeriod || item?.toPeriod) === today
  })
}

/** Main / whole-house history: do not sit on last 01 Cookies. */
export const shouldSkipLastSupplierCreatePrefill = (historyCountType = 'ALL') =>
  String(historyCountType || 'ALL').toUpperCase() === 'MAIN'

/** Count-again hero lists a supplier take; hide it on Main history. */
export const shouldShowCountAgainHero = (historyCountType = 'ALL') =>
  !shouldSkipLastSupplierCreatePrefill(historyCountType)

export const pickOpenRepeatableStockTake = (stockTakes = [], options) => {
  const latest = pickLatestRepeatableStockTake(stockTakes, options)
  if (!latest) return null
  return findOpenStockTakeForRepeat(latest, stockTakes, options)
}

/** Prefill create with last supplier + name. Does not POST a take. */
export const prefillStockTakeCreateFromLastSupplier = (
  stockTakes = [],
  options = {},
) => {
  if (shouldSkipLastSupplierCreatePrefill(options.historyCountType)) {
    return { supplierIds: [], name: '' }
  }
  const list = Array.isArray(stockTakes) ? stockTakes : []
  for (const stockTake of list) {
    if (!canRepeatStockTake(stockTake, options)) continue
    if (findOpenStockTakeForRepeat(stockTake, stockTakes, options)) continue
    const payload = buildStockTakeRepeatPayload(stockTake, options)
    if (!payload?.supplierIds?.length) continue
    if (createPrefillClashesToday(payload, stockTakes, options.now)) continue
    return {
      supplierIds: uniqueStockTakeSupplierIds(payload.supplierIds),
      name: String(payload.name || '').trim(),
    }
  }
  return { supplierIds: [], name: '' }
}

export const repeatLastStockTakeLabel = stockTake => {
  const titles = Array.isArray(stockTake?.supplierTitles)
    ? [
        ...new Set(
          stockTake.supplierTitles
            .map(title => String(title || '').trim())
            .filter(Boolean),
        ),
      ]
    : []
  if (titles.length) return titles.join(', ')
  return String(stockTake?.name || '').trim() || 'last supplier count'
}

export const stockTakeRepeatAgainLabel = stockTake =>
  `Count ${repeatLastStockTakeLabel(stockTake)} again.`

export const stockTakeOpenTodayLabel = stockTake =>
  `Open today’s ${repeatLastStockTakeLabel(stockTake)}`

export const isStockTakeSupplierCreateReady = args =>
  stockTakeCreateHasSuppliers(args)

export const isStockTakeHouseCreateReady = args =>
  Boolean(args?.startDate && args?.endDate) &&
  !stockTakeCreateHasSuppliers(args)

export const listStockTakeCreateSupplierOptions = (venueSuppliers = []) => {
  const options = []
  const seen = new Set()
  venueSuppliers.forEach(venueSupplier => {
    if (!venueSupplier || venueSupplier.nonOMSSupplier === true) return
    if (venueSupplier.isActive === false) return
    const id = idFrom(venueSupplier.supplier)
    if (!OBJECT_ID_PATTERN.test(id) || seen.has(id)) return
    seen.add(id)
    const label =
      String(
        venueSupplier.supplierTitle ||
          venueSupplier.supplier?.title ||
          venueSupplier.title ||
          '',
      ).trim() || 'Supplier'
    options.push({ value: id, label })
  })
  return options.sort((a, b) => a.label.localeCompare(b.label))
}
